import { setGlobalCSSVariables } from 'helpers/styleHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import { useRouter } from 'next/router';
import styles from 'pages/[communityId]/index.module.scss';
import { useTranslation } from 'react-i18next';
import { SCREEN_LG } from 'utils/constants';
import { pathnames } from 'utils/routerHelpers';

import React, { PropsWithChildren, useEffect } from 'react';

import { classNames } from '@thinkific/chalk/utils';

import CommunityBrandingBadge from 'components/CommunityBrandingBadge';
import CommunityChecklist from 'components/CommunityChecklist';
import { useCommunityContext } from 'components/CommunityContext';
import CourseDetails from 'components/CourseCard/CourseDetails';
import EventPreviewCard from 'components/Events/components/EventPreviewCard/EventPreviewCard';
import { useSiteFeaturesContext } from 'components/SiteFeaturesContext';
import { SpaceDetails } from 'components/SpaceDetails';
import TrendingPosts from 'components/TrendingPosts/TrendingPosts';
import VisibilityCloak from 'components/VisibilityCloak/VisibilityCloak';

import * as css from './PageContent.css';

const cx = classNames.bind(styles);

export type PageContentProps = {
  hasContextualInfo?: boolean;
  withLayout?: boolean;
  children: PropsWithChildren<React.ReactNode>;
  className?: string;
};

export const PageContent = ({
  children,
  className,
  hasContextualInfo = true,
  withLayout = true,
}: PageContentProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { pathname } = router;
  const { width } = useWindowSize();
  const { community, site } = useCommunityContext();
  const { exceededPlanSpaces } = useSiteFeaturesContext();

  useEffect(() => {
    setGlobalCSSVariables(community.primaryColor);
  }, [community]);

  const spaceDetailPages = [pathnames.spaces.space, pathnames.spaces.new, pathnames.spaces.edit];
  const courseCardPages = [pathnames.spaces.space, pathnames.spaces.new, pathnames.spaces.edit];
  const eventCardPages = [pathnames.events.new, pathnames.events.edit];
  const isHomePage = pathname === pathnames.home;
  const isNewSpacePage = pathname === pathnames.spaces.new;

  const showSpaceDetails = spaceDetailPages.includes(pathname);
  const includeCourseCard = courseCardPages.includes(pathname);
  const includeEventCard = eventCardPages.includes(pathname);

  const showContextualInfo = width >= SCREEN_LG && hasContextualInfo;
  const spaceId = router.query?.spaceId as string;

  const shouldShowCommunitiesChecklist = !community.published && isHomePage;

  if (!withLayout) return <>{children}</>;

  return (
    <section className={classNames(css.main, className)} data-qa="main-content">
      <div className={css.layout}>
        <div className={css.content}>{children}</div>

        {showContextualInfo && (
          <aside aria-label={t('Extra information')} className={css.aside} data-qa="right-content">
            {showSpaceDetails && (
              <SpaceDetails
                className={cx('desktop-navigation')}
                communityId={community.id}
                spaceId={spaceId}
              />
            )}
            {includeCourseCard && (
              <VisibilityCloak hide={isNewSpacePage && exceededPlanSpaces}>
                <CourseDetails />
              </VisibilityCloak>
            )}
            {includeEventCard && <EventPreviewCard />}
            {community.published && isHomePage && <TrendingPosts />}
            {shouldShowCommunitiesChecklist && (
              <VisibilityCloak admins>
                <CommunityChecklist />
              </VisibilityCloak>
            )}
            <CommunityBrandingBadge site={site} />
          </aside>
        )}
      </div>
    </section>
  );
};

export default PageContent;
